<script lang="ts" setup>
import { computed, ref } from 'vue';
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/vue';
import DesktopIcon from './icons/DesktopIcon.vue';
import TabletIcon from './icons/TabletIcon.vue';
import MobileIcon from './icons/MobileIcon.vue';
import { useElementBounding } from '@vueuse/core';

const listDevices = {
  desktop: DesktopIcon,
  tablet: TabletIcon,
  mobile: MobileIcon,
};

type ScreenType = keyof typeof listDevices;

type Props = {
  controlType?: string;
  currentScreen?: ScreenType;
};

const props = withDefaults(defineProps<Props>(), {
  currentScreen: 'desktop',
});

const emit = defineEmits<{
  (e: 'changeScreen', screenId: ScreenType): void;
}>();

// Methods
const controlHasDevices = computed(() => {
  const typeInvisible = ['visibility'];
  return !typeInvisible.includes(props.controlType || '');
});
const devices: ScreenType[] = ['desktop', 'tablet', 'mobile'];

const setActiveScreenId = (screenId: ScreenType) => {
  emit('changeScreen', screenId);
};

const menuButton = ref<HTMLElement>();
let isMenuItemBottom = false;

const onClick = () => {
  const { bottom } = useElementBounding(menuButton);
  if (window.innerHeight - bottom.value < 105) {
    isMenuItemBottom = true;
  }
};
</script>

<template>
  <div class="flex items-center">
    <Menu v-if="controlHasDevices" as="div" class="relative ml-[9px] flex items-center">
      <MenuButton
        ref="menuButton"
        class="text-dark-low inline-flex w-full justify-center bg-opacity-20 font-medium transition duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
        @click="onClick">
        <component :is="listDevices[currentScreen]" class="text-dark-disabled"></component>
      </MenuButton>
      <MenuItems
        class="bg-dark-200 shadow-4dp rounded-medium absolute right-[-12px] z-[100] origin-top-right p-4 focus:outline-none"
        :class="[isMenuItemBottom ? 'bottom-16' : 'top-16']">
        <MenuItem v-for="device in devices" :key="device">
          <button
            class="rounded-medium group flex w-full items-center justify-center p-8 transition-all duration-200"
            :class="{
              'text-dark-high cursor-default': device == currentScreen,
              'text-dark-low hover:bg-light-100/20 cursor-pointer': device != currentScreen,
            }"
            @click="setActiveScreenId(device)">
            <component :is="listDevices[device]"></component>
          </button>
        </MenuItem>
      </MenuItems>
    </Menu>
  </div>
</template>
